import React, { useContext, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import TextareaAutosize from 'react-textarea-autosize';
import classNames from 'classnames';
import isNull from 'lodash/isNull';
import { v4 as uuid } from 'uuid';

import styles from './Textarea.module.scss';

import { FeatureName } from 'chaika-enums';

import { translate } from '@chaika/i18n/helper';
import { DictationButton, DictationButtonSlotContext, useDictation } from '@components/Dictation';
import { DynamicTitle } from '@components/DynamicTitle';
import { NotValidFieldText } from '@components/NotValidFieldText';
import { LeftTitle } from '@components/LeftTitle';
import { FieldTitle, FieldTitleTheme } from '@components/FieldTitle';
import { AutoResizeTextarea } from '@components/Textarea/AutosizedTextArea';
import { featureToggleService } from '@store/Configuration/featureToggleService';

import { isDefined } from '@utils/isDefined';

import { TextareaProps, TextAreaType } from './types';

export function Textarea({
    title,
    description,
    minRows,
    placeholder,
    leftTitle,
    ref,
    boldTitle,
    withoutBorders,
    minHeight,
    showValidStatus = true,
    modifiers = [],
    isValid = null,
    notValidText,
    disabled,
    textareaRef,
    dataTest,
    isRequired = false,
    type = TextAreaType.TextareaAutosize,
    defaultValue,
    value,
    readOnly,
    withDictation,
    onChange,
    onChangeText,
    onFocus,
    onBlur,
    name,
    ...props
}: TextareaProps) {
    const localRef = useRef<HTMLTextAreaElement>(null);
    const textareaRefToUse = (textareaRef ?? localRef) as React.RefObject<HTMLTextAreaElement>;
    const [internalValue, setInternalValue] = useState<string>(defaultValue ?? '');
    const [isActive, setIsActive] = useState<boolean>(false);

    const controlled = isDefined(value);
    const currentValue = controlled ? value : internalValue;

    const slotNode = useContext(DictationButtonSlotContext);

    const [dictationFieldId] = useState(() => uuid());
    const isDictationEnabled =
        Boolean(withDictation) &&
        !disabled &&
        featureToggleService.isAvailableForAuthUser(FeatureName.RealtimeDictation);
    const dictation = useDictation(
        dictationFieldId,
        () => currentValue ?? '',
        text => {
            if (!controlled) {
                setInternalValue(text);
            }
            onChangeText?.(text);
        },
        isDictationEnabled,
    );

    const effectiveReadOnly = readOnly || dictation.isDictating;

    if (controlled && isDefined(defaultValue)) {
        throw new Error(
            `Invariant violation: Encountered 'defaultValue' in a controlled component (with 'value' passed)`,
        );
    }

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (!controlled) {
            setInternalValue(event.target.value);
        }
        onChange?.(event);
    };

    const handleFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
        onFocus?.(event);
        setIsActive(true);
    };

    const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
        onBlur?.(event);
        setIsActive(false);
    };

    const withValidStatus = showValidStatus && !isNull(isValid);

    const isRedesigned = !withoutBorders;

    const displayTitle = title && isRequired ? `${title} *` : title;

    const hasInternalTitleRow = Boolean(displayTitle) && !leftTitle && isRedesigned;
    const dictationPlacement = !isDictationEnabled
        ? 'none'
        : slotNode
          ? 'portal'
          : slotNode === null
            ? 'pending'
            : hasInternalTitleRow
              ? 'title'
              : 'floating';

    const dictationButton = (
        <DictationButton
            isDictating={dictation.isDictating}
            isStarting={dictation.isStarting}
            error={dictation.error}
            hasFailedUtterance={dictation.hasFailedUtterance}
            onClick={dictation.toggleDictation}
        />
    );

    const textarea = (
        <div
            className={classNames([
                styles.root,
                isRedesigned && styles.root_redesign,
                isActive && styles.active,
                disabled && styles.disabled,
                withValidStatus && (isValid ? styles.valid : styles.notValid),
                dictation.isDictating && !dictation.isStarting && styles.dictating,
                ...modifiers,
            ])}
        >
            {displayTitle && !leftTitle && (
                <div
                    className={classNames([
                        isRedesigned && styles.title,
                        dictationPlacement === 'title' && styles.titleWithDictation,
                    ])}
                >
                    {!isRedesigned ? (
                        <DynamicTitle
                            title={displayTitle}
                            disabled={disabled}
                            boldTitle={boldTitle}
                            isOpen={!!currentValue || isActive}
                            isValid={withValidStatus ? isValid : null}
                        />
                    ) : (
                        <FieldTitle
                            theme={FieldTitleTheme.WithFlex}
                            title={displayTitle}
                        />
                    )}
                    {dictationPlacement === 'title' ? dictationButton : null}
                </div>
            )}
            {description && <div className={styles.description}>{description}</div>}
            <div className={styles.textareaWrapper}>
                {type === TextAreaType.TextareaAutosize ? (
                    <TextareaAutosize
                        value={currentValue}
                        className={styles.textarea}
                        minRows={minRows ?? (isRedesigned ? 3 : 2)}
                        placeholder={placeholder ?? ''}
                        disabled={disabled}
                        readOnly={effectiveReadOnly}
                        data-test={dataTest}
                        data-testid={dataTest}
                        ref={textareaRefToUse as any}
                        name={name}
                        {...(props as any)}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    />
                ) : type === TextAreaType.NewTextAreaAutosize ? (
                    <AutoResizeTextarea
                        {...props}
                        value={currentValue}
                        className={styles.textarea}
                        minRows={minRows ?? 3}
                        placeholder={placeholder ?? ''}
                        disabled={disabled}
                        readOnly={effectiveReadOnly}
                        data-test={dataTest}
                        data-testid={dataTest}
                        ref={textareaRefToUse}
                        name={name}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    />
                ) : (
                    <textarea
                        value={currentValue}
                        className={classNames([styles.textarea, styles.textareaFixed])}
                        disabled={disabled}
                        readOnly={effectiveReadOnly}
                        data-test={dataTest}
                        data-testid={dataTest}
                        ref={textareaRefToUse}
                        name={name}
                        {...(props as any)}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    />
                )}
                {dictation.isStarting ? (
                    <div className={styles.dictationConnecting}>{translate('dictationConnecting')}</div>
                ) : null}
                {dictationPlacement === 'floating' ? (
                    <div className={styles.dictationButton}>{dictationButton}</div>
                ) : null}
                {dictationPlacement === 'portal' && slotNode ? createPortal(dictationButton, slotNode) : null}
            </div>

            {withValidStatus && !isValid && notValidText && <NotValidFieldText title={notValidText} />}
        </div>
    );

    return leftTitle && displayTitle ? (
        <LeftTitle
            title={displayTitle}
            customTitleTop={10}
        >
            {textarea}
        </LeftTitle>
    ) : (
        textarea
    );
}
